
import React, {
  lazy, Suspense, useCallback, useEffect, useState,
} from 'react';
import { Provider, useDispatch } from 'react-redux';
import {
  BrowserRouter
} from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import {
  Box, CssBaseline, ThemeProvider, createTheme,
} from '@mui/material';
import { logoutAction, resumeLastSessionAction, } from '@ivymobi/orbit-core/business/user';
import configureStore from '@ivymobi/orbit-core/business/configureStore';
import i18n from './services/i18n';
import * as api from './api';
import themeConfig from './services/theme';
import Loader from './components/loaders';
import { InnerUserContext, ThemeContext, UserContext, IframeLoginContext } from './orbit-context';
import { queryParse } from '@ivymobi/orbit-core';
import { createStyles, makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import PreProcess from './components/preProcess';
import Notification from './layout/notification';
import { IframeParamsInfo } from './types';
import MessageCenter from './components/MeesageCentter';
import WXDownloadMaskView from './components/WXDownloadMaskView';
import { startMitutoyoHeightBridge } from './integration/mitutoyoBridge';

const ActiveContainer = lazy(
  () => import('./ActiveContainer' /* webpackChunkName: "ActiveContainer" */)
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      height: '100%',
      width: '100%',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.getContrastText(
        theme.palette.background.default
      ),
    },
  })
);

const Main = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const innerUser = queryParse(window.location.search) as IframeParamsInfo;
  const iframeLogin = window.self !== window.top;

  const [user, setUser] = useState(null);

  useEffect(() => {
    const bridge = startMitutoyoHeightBridge({
      window,
      document,
      rootUrl: innerUser?.RootURL,
    });
    return bridge.cleanup;
  }, [innerUser?.RootURL]);

  useEffect(() => {
    dispatch(resumeLastSessionAction()).then(
      (userData) => {
        userData.logout = logout;
        setUser(userData);
      },
      () => setUser({}));
  }, []);

  const doLogin = (userData) => {
    userData.logout = logout;
    setUser(userData);
  };

  const logout = useCallback(() => {
    dispatch(logoutAction())
    .then(() => {
      setUser({});
    });
  }, [dispatch, setUser]);

  return (
    <Box className={classes.root}>
      <UserContext.Provider value={user}>
        <InnerUserContext.Provider value={innerUser}>
          <IframeLoginContext.Provider value={iframeLogin}>
            <Suspense fallback={<Loader />}>
              <WXDownloadMaskView>
                <ActiveContainer doLogin={doLogin} logout={logout}/>
              </WXDownloadMaskView>
            </Suspense>
          </IframeLoginContext.Provider>
        </InnerUserContext.Provider>
      </UserContext.Provider>
    </Box>
  );
};

export default function OrbitApp() {
  const store = configureStore(api, {});

  themeConfig.palette.primary.main = '#005ae7';
  const theme = createTheme(themeConfig);

  const handleChangeTheme = (color) => {
    theme.palette.primary.main = color;
  };

  return (

    <BrowserRouter>
      <ThemeContext.Provider value={handleChangeTheme}>
        <CssBaseline />
        <ThemeProvider theme={theme}>
          <Provider store={store}>
            <PreProcess>
              <I18nextProvider i18n={i18n}>
                <Suspense fallback={<Loader />} >
                  <MessageCenter>
                    <Main />
                  </MessageCenter>
                  <Notification />
                </Suspense>
              </I18nextProvider>
            </PreProcess>
          </Provider>
        </ThemeProvider>
      </ThemeContext.Provider>
    </BrowserRouter>
  );
}
